import { ChevronDown, ChevronUp } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "../../../../shared/components/ui/button.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../../../shared/components/ui/sheet.js";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../shared/components/ui/tabs.js";
import {
  isSaleReopenRequestedNotification,
  parseSaleReopenNotificationDeeplink,
} from "../../sale/reopen/index.js";
import {
  NOTIFICATION_INBOX_TAB_LABELS,
  NOTIFICATION_INBOX_TABS,
  notificationsConfig,
} from "../config/notifications.config.js";
import { canHaveNotificationActions } from "../lib/can-have-notification-actions.js";
import { sortPendingNotifications } from "../lib/sort-pending-notifications.js";
import { useNotificationsStore } from "../stores/use-notifications-store.js";
import { NotificationCard } from "./notification-card.js";
import { LoadingState } from "../../../../shared/components/feedback/loading/index.js";

/**
 * @param {Object} props
 * @param {Object[]} props.items
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {() => void} [props.onRetry]
 * @param {(id: string) => void} [props.onOpenNotification]
 * @param {(id: string) => void} [props.onMarkReadNotification]
 * @param {string|null} props.markingId
 * @param {boolean} props.canReopen
 * @param {string|null} props.reopenBusyId
 * @param {(id: string) => void|Promise<void>} [props.onApproveReopen]
 * @param {(id: string, reason?: string) => void|Promise<void>} [props.onRejectReopen]
 * @param {boolean} [props.readOnly]
 * @param {string} [props.emptyMessage]
 */
function NotificationsList({
  items,
  isLoading,
  error,
  onRetry,
  onOpenNotification,
  onMarkReadNotification,
  markingId,
  canReopen,
  reopenBusyId,
  onApproveReopen,
  onRejectReopen,
  readOnly = false,
  emptyMessage = "Nenhuma notificação por aqui",
}) {
  if (isLoading && items.length === 0) {
    return (
      <LoadingState message="Carregando notificações..." className="py-10" />
    );
  }

  if (!isLoading && error) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <p className="text-sm text-muted-foreground">
          Erro ao buscar notificações
        </p>
        {onRetry ? (
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Tentar novamente
          </Button>
        ) : null}
      </div>
    );
  }

  if (!isLoading && !error && items.length === 0) {
    return (
      <p className="py-10 text-center text-sm text-muted-foreground">
        {emptyMessage}
      </p>
    );
  }

  if (error || items.length === 0) return null;

  return (
    <ul className="space-y-2">
      {items.map((item) => {
        const showReopenActions =
          !readOnly &&
          canHaveNotificationActions(item) &&
          canReopen &&
          isSaleReopenRequestedNotification(item.type);
        const parsed = parseSaleReopenNotificationDeeplink(
          item.deeplink || "",
        );
        const saleId = item.saleId || parsed.saleId;
        const notifyUserId =
          item.requesterUserId ?? parsed.notifyUserId ?? null;
        const canSubmitReopen = Boolean(saleId) && notifyUserId != null;

        return (
          <li key={item.id}>
            <NotificationCard
              notification={item}
              readOnly={readOnly}
              onOpen={readOnly ? undefined : onOpenNotification}
              onMarkRead={readOnly ? undefined : onMarkReadNotification}
              isMarking={markingId === item.id}
              showReopenActions={showReopenActions}
              canSubmitReopen={canSubmitReopen}
              isReopenBusy={reopenBusyId === item.id}
              onApproveReopen={
                readOnly ? undefined : () => onApproveReopen?.(item.id)
              }
              onRejectReopen={
                readOnly
                  ? undefined
                  : (reason) => onRejectReopen?.(item.id, reason)
              }
            />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Painel lateral com abas Pendentes / Lidas.
 * Lê os dois slices diretamente do store Zustand.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {boolean} [props.isLoading]
 * @param {string|null} [props.error]
 * @param {() => void} [props.onRetry]
 * @param {(id: string) => void} [props.onOpenNotification]
 * @param {(id: string) => void} [props.onMarkReadNotification]
 * @param {string|null} [props.markingId]
 * @param {boolean} [props.canReopen]
 * @param {string|null} [props.reopenBusyId]
 * @param {(id: string) => void|Promise<void>} [props.onApproveReopen]
 * @param {(id: string, reason?: string) => void|Promise<void>} [props.onRejectReopen]
 */
export function NotificationsSidebar({
  isOpen,
  onClose,
  isLoading = false,
  error = null,
  onRetry,
  onOpenNotification,
  onMarkReadNotification,
  markingId = null,
  canReopen = false,
  reopenBusyId = null,
  onApproveReopen,
  onRejectReopen,
}) {
  const [activeTab, setActiveTab] = useState(NOTIFICATION_INBOX_TABS.pending);
  const [showAllPending, setShowAllPending] = useState(false);
  const [showAllRead, setShowAllRead] = useState(false);

  const pendingItems = useNotificationsStore((state) => state.pendingItems);
  const pendingNextCursor = useNotificationsStore((state) => state.pendingNextCursor);
  const readItems = useNotificationsStore((state) => state.readItems);
  const readNextCursor = useNotificationsStore((state) => state.readNextCursor);
  const readIsLoading = useNotificationsStore((state) => state.readIsLoading);
  const fetchMorePending = useNotificationsStore((state) => state.fetchMorePending);
  const fetchRead = useNotificationsStore((state) => state.fetchRead);
  const fetchMoreRead = useNotificationsStore((state) => state.fetchMoreRead);

  const PENDING_LIMIT = notificationsConfig.pendingInboxLimit;
  const READ_LIMIT = notificationsConfig.readInboxLimit;

  const sortedPending = sortPendingNotifications(pendingItems);
  const visiblePendingItems = showAllPending
    ? sortedPending
    : sortedPending.slice(0, PENDING_LIMIT);

  const hiddenPendingCount = sortedPending.length - PENDING_LIMIT;

  const visibleReadItems = showAllRead ? readItems : readItems.slice(0, READ_LIMIT);
  const hiddenReadCount = readItems.length - READ_LIMIT;

  useEffect(() => {
    if (
      activeTab === NOTIFICATION_INBOX_TABS.essentialRead &&
      readItems.length === 0 &&
      !readIsLoading
    ) {
      void fetchRead();
    }
  }, [activeTab, readItems.length, readIsLoading, fetchRead]);

  const listProps = {
    isLoading,
    error,
    onRetry,
    markingId,
    canReopen,
    reopenBusyId,
  };

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose?.();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
      >
        <SheetHeader className="border-b px-6 py-4 text-left">
          <SheetTitle>Notificações</SheetTitle>
          <SheetDescription className="sr-only">
            Lista de notificações do usuário
          </SheetDescription>
        </SheetHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="">
            <TabsList className="grid h-9 w-full grid-cols-2">
              <TabsTrigger value={NOTIFICATION_INBOX_TABS.pending}>
                {NOTIFICATION_INBOX_TAB_LABELS[NOTIFICATION_INBOX_TABS.pending]}
              </TabsTrigger>
              <TabsTrigger value={NOTIFICATION_INBOX_TABS.essentialRead}>
                {NOTIFICATION_INBOX_TAB_LABELS[NOTIFICATION_INBOX_TABS.essentialRead]}
              </TabsTrigger>
            </TabsList>
          </div>

          {/* ── Aba Pendentes ── */}
          <TabsContent
            value={NOTIFICATION_INBOX_TABS.pending}
            className="mt-0 flex-1 overflow-y-auto px-4 py-3 data-[state=inactive]:hidden"
          >
            <NotificationsList
              {...listProps}
              items={visiblePendingItems}
              onOpenNotification={onOpenNotification}
              onMarkReadNotification={onMarkReadNotification}
              onApproveReopen={onApproveReopen}
              onRejectReopen={onRejectReopen}
              emptyMessage="Nenhuma notificação pendente"
            />

            {!showAllPending && (hiddenPendingCount > 0 || pendingNextCursor) && (
              <button
                type="button"
                onClick={() => {
                  setShowAllPending(true);
                  if (pendingNextCursor) void fetchMorePending();
                }}
                className="mt-3 flex w-full items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ChevronDown className="h-3.5 w-3.5" />
                {hiddenPendingCount > 0
                  ? `Ver mais (${hiddenPendingCount} oculta${hiddenPendingCount !== 1 ? "s" : ""})`
                  : "Ver mais"}
              </button>
            )}

            {showAllPending && sortedPending.length > PENDING_LIMIT && (
              <button
                type="button"
                onClick={() => setShowAllPending(false)}
                className="mt-3 flex w-full items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ChevronUp className="h-3.5 w-3.5" />
                Ver menos
              </button>
            )}
          </TabsContent>

          {/* ── Aba Lidas ── */}
          <TabsContent
            value={NOTIFICATION_INBOX_TABS.essentialRead}
            className="mt-0 flex-1 overflow-y-auto px-4 py-3 data-[state=inactive]:hidden"
          >
            <NotificationsList
              {...listProps}
              isLoading={readIsLoading}
              error={null}
              items={visibleReadItems}
              readOnly
              emptyMessage="Nenhuma notificação essencial lida"
            />

            {!showAllRead && (hiddenReadCount > 0 || readNextCursor) && (
              <button
                type="button"
                onClick={() => {
                  setShowAllRead(true);
                  if (readNextCursor) void fetchMoreRead();
                }}
                className="mt-3 flex w-full items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ChevronDown className="h-3.5 w-3.5" />
                {hiddenReadCount > 0
                  ? `Ver mais (${hiddenReadCount} oculta${hiddenReadCount !== 1 ? "s" : ""})`
                  : "Ver mais"}
              </button>
            )}

            {showAllRead && readItems.length > READ_LIMIT && (
              <button
                type="button"
                onClick={() => setShowAllRead(false)}
                className="mt-3 flex w-full items-center justify-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <ChevronUp className="h-3.5 w-3.5" />
                Ver menos
              </button>
            )}
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
